import { Box } from '@mui/material';
import { useStyles } from './ReportCard.styles';
import Typography from '../../../../../../components/v2/Typography';
import clsx from 'clsx';
import { iconBtnPosition, iconBtnType, IconButton } from '../../../../molecules/icon-button';
import MultiColorProgressBar from '../../../../../../pages/admin/notes/multi-color-progress-bar/MultiColorProgressBar';
import { Icons } from '../../../../../icons';
import { colors } from '../../../../../colors';
import { btnType, Button } from '../../../../atoms/button';
import StarEmoji from '../../../../../../assets/images/star-emoji.png';
import ThinkEmoji from '../../../../../../assets/images/thinking-emoji.png';
import { useNavigate } from 'react-router-dom';
const ReportCard = ({ openQuickSummary, sessionReportCard, waitingForAI = false }) => {
  const classes = useStyles();
  const navigate = useNavigate();

  const sessionData = [
    {
      name: 'Open-ended questions',
      value: sessionReportCard?.reportCard?.sessionComposition?.openEndedQuestions ?? 0,
      color: '#6172F3',
      bgColor: '#E0EAFF',
      fontColor: '#6172F3',
    },
    {
      name: 'Psychoeducation',
      value: sessionReportCard?.reportCard?.sessionComposition?.psychoEducation ?? 0,
      color: '#F63D68',
      bgColor: '#FFE4E8',
      fontColor: '#F63D68',
    },
    {
      name: 'Small Talk',
      value: sessionReportCard?.reportCard?.sessionComposition?.smallTalk ?? 0,
      color: '#16B364',
      bgColor: '#D3F8DF',
      fontColor: '#16B364',
    },
    {
      name: 'Other',
      value: sessionReportCard?.reportCard?.sessionComposition?.other ?? 0,
      color: '#06AED4',
      bgColor: '#CFF8FE',
      fontColor: '#06AED4',
    },
  ];

  const idealSession = [
    {
      name: 'Open-ended questions',
      value: sessionReportCard?.idealComposition?.openEndedQuestions ?? 0,
      color: '#C6D7FE',
      bgColor: '#E0EAFF',
      fontColor: '#6172F3',
    },
    {
      name: 'Psychoeducation',
      value: sessionReportCard?.idealComposition?.psychoEducation ?? 0,
      color: '#FECCD6',
      bgColor: '#FFE4E8',
      fontColor: '#F63D68',
    },
    {
      name: 'Small Talk',
      value: sessionReportCard?.idealComposition?.smallTalk ?? 0,
      color: '#AAF0C4',
      bgColor: '#D3F8DF',
      fontColor: '#16B364',
    },
    {
      name: 'Other',
      value: sessionReportCard?.idealComposition?.other ?? 0,
      color: '#A5F0FC',
      bgColor: '#CFF8FE',
      fontColor: '#06AED4',
    },
  ];
  const renderSessionData = (title, emoji, data) => (
    <Box display="flex" alignItems="center" gap="24px">
      <Box maxWidth="64px" maxHeight="64px">
        <img src={emoji} alt="emoji" style={{ width: '100%', height: '100%' }} />
      </Box>
      <Box width="90%" gap="16px" display="flex" flexDirection="column">
        <Typography variant="h6" className={classes.title}>
          {title}
        </Typography>
        <MultiColorProgressBar readings={data} totalValue={100} isRadius={false} />
        <Box
          display="flex"
          gap="24px"
          flexWrap="wrap"
          sx={{
            maxWidth: '85%',
            '@media (min-width:1759px)': {
              maxWidth: '60%',
            },
            '@media (min-width:2143px)': {
              maxWidth: '41%',
            }
          }}
        >
          {data.map(({ name, value, bgColor, fontColor }, index) => (
            <Box key={index} display="flex" gap="12px" className={classes.dataItem}>
              <Box
                style={{ background: bgColor, color: fontColor }}
                className={classes.dataValue}
              >{`${value}%`}</Box>
              <Box>{name}</Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  const defaultTherapeuticAlliance = [
    {
      text: 'You greeted the patient and asked how they have been doing',
      completed: false,
      supportingQuote: '',
    },
    {
      text: 'You asked if anything interesting or important has happened since the last appointment',
      completed: false,
      supportingQuote: '',
    },
    {
      text: 'You asked if they completed any homework since the last session',
      completed: false,
      supportingQuote: '',
    },
    {
      text: 'You asked how they felt the session went',
      completed: false,
      supportingQuote: '',
    },
  ];

  const defaultTreatmentPlan = [
    {
      text: 'You reviewed the treatment goals to ensure they are still relevant',
      completed: false,
      supportingQuote: '',
    },
    {
      text: 'You discussed the next appointment with the patient',
      completed: false,
      supportingQuote: '',
    },
    {
      text: 'You discussed what the patient should be doing in between this appointment and the next appointment',
      completed: false,
      supportingQuote: '',
    },
  ];

  const renderQuickSummary = () => {
    return (
      <Box className={classes.card}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box className={classes.title}>Patient Quick Summary</Box>
          <Box>
            <IconButton
              className={classes.editBtn}
              icon="edit-underline"
              variant="text"
              onClick={openQuickSummary}
              iconPosition="left"
            >
              Edit
            </IconButton>
          </Box>
        </Box>
        <Box className={classes.cardText}>{sessionReportCard?.reportCard?.patientSummary}</Box>
      </Box>
    );
  };

  const renderTherapeuticAlliance = () => {
    const therapeuticAlliance =
      sessionReportCard?.reportCard?.therapeuticAlliance?.items ?? defaultTherapeuticAlliance;
    return (
      <Box className={classes.card}>
        <Box display="flex" alignItems="center" gap="12px">
          <Box width={22} maxWidth={22} maxHeight={22}>
            <Icons glyph="star" color="#6172F3" className={classes.visitFrequency} />
          </Box>
          <Box className={classes.title}>Building therapeutic alliance</Box>
        </Box>
        {therapeuticAlliance?.map((item, index) => (
          <Box key={index}>
            <Box>
              <Box
                className={clsx(classes.cardItem, classes.cardItemCheckList, {
                  [classes.cardBorderBottom]:
                    !item.supportingQuote && index !== therapeuticAlliance?.length - 1,
                })}
              >
                <Box className={classes.cardItemText}>{item.text}</Box>
                <Icons
                  glyph={item?.completed ? 'check-circle' : 'clear-circle'}
                  className={item?.completed ? classes.checkIcon : classes.crossIcon}
                />
              </Box>
            </Box>
            {item?.supportingQuote && (
              <Box
                className={clsx({
                  [classes.cardQuoteBorderBottom]:
                    item.supportingQuote && index !== therapeuticAlliance?.length - 1,
                })}
              >
                <Box className={clsx(classes.cardSubTitle, classes.cardQuote)} alignItems="center">
                  <Icons glyph="ai-quote" color={colors.neutral400} />
                  <Box>{item?.supportingQuote}</Box>
                </Box>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const renderTreatmentPlan = () => {
    const treatmentPlan = sessionReportCard?.reportCard?.treatmentPlan?.items ?? defaultTreatmentPlan;
    return (
      <Box className={classes.card}>
        <Box display="flex" alignItems="center" gap="12px">
          <Box className={classes.title}>Treatment plan and planning</Box>
        </Box>
        {treatmentPlan?.map((item, index) => (
          <Box key={index}>
            <Box>
              <Box
                className={clsx(classes.cardItem, classes.cardItemCheckList, {
                  [classes.cardBorderBottom]: !item.supportingQuote && index !== treatmentPlan?.length - 1,
                })}
              >
                <Box className={classes.cardItemText}>{item.text}</Box>
                <Icons
                  glyph={item?.completed ? 'check-circle' : 'clear-circle'}
                  className={item?.completed ? classes.checkIcon : classes.crossIcon}
                />
              </Box>
            </Box>
            {item?.supportingQuote && (
              <Box
                className={clsx({
                  [classes.cardQuoteBorderBottom]:
                    item.supportingQuote && index !== treatmentPlan?.length - 1,
                })}
              >
                <Box className={clsx(classes.cardSubTitle, classes.cardQuote)} alignItems="center">
                  <Icons glyph="ai-quote" color={colors.neutral400} />
                  <Box>{item?.supportingQuote}</Box>
                </Box>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap="16px" flex={2}>
      {waitingForAI && (
        <Box className={classes.warningBox}>
          <Box className={classes.warningBoxContent}>
            <Icons glyph="clock" className={classes.iconSize} color="#6172F3" />
            <span>
              Your report card hasn't been completed yet -{' '}
              <span className={classes.bold}>Please give us a few mins</span>
            </span>
          </Box>
          <Button
            className={classes.backBtn}
            variant={iconBtnType.TEXT}
            onClick={() => navigate('/provider/todos')}
            iconPosition={iconBtnPosition.RIGHT}
          >
            Back to To Dos
          </Button>
        </Box>
      )}
      <Box display="flex" flexDirection="column" gap="24px" width="100%" height="100%">
        <Box className={classes.sessionCard}>
          {renderSessionData('Your session', ThinkEmoji, sessionData)}
          {renderSessionData('Ideal session', StarEmoji, idealSession)}
        </Box>
        {renderQuickSummary()}
        {renderTherapeuticAlliance()}
        {renderTreatmentPlan()}
      </Box>
    </Box>
  );
};

export { ReportCard };
